import React, { useState, useContext } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-fluent-component/wizard';
import { useFormService, FORM_STATES } from '@bingads-webui-fluent-component/form';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { StepHeader } from '@bingads-webui-campaign-react/linkedin-shared-components';
import { LINKEDIN_WIZARD_STEP_PATHS } from '@bingads-webui-campaign-react/linkedin-utils';
import {
  useLinkedInAdAdapter,
  createOrUpdateAd,
  genericApiError,
  mapBulkUpsertErrors,
  getBulkUploadErrorType,
  ERROR_ENTITY,
} from '@bingads-webui-campaign-react/linkedin-data';
import { MultiAdsManager } from '@bingads-webui-campaign-react/multi-ads-manager';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';

import { ResponsiveAd } from '@bingads-webui/campaign-odata-schemas';

import { AdPreview } from './components/ad-preview';
import { getAdsManagerStepStyle } from './styles/ads-manager-step-styles';

const bulkUpsertEntityErrorMap = {
  [getBulkUploadErrorType(ResponsiveAd)]: ERROR_ENTITY.Ad,
};

export const LinkedInAdsManagerStep = ({
  data,
  onDataChange,
}) => {
  const i18n = useContext(I18nContext);
  const { send } = useWizardService();
  const { mapToBulkModel: mapLinkedInAdToBulkModel } = useLinkedInAdAdapter();
  const classes = useDefaultStyles(getAdsManagerStepStyle);

  const {
    campaignId,
    adGroupId,
    ads,
    connectedCompanyPage,
  } = data || {};

  const { state } = useFormService();
  const isSaving = state.matches(FORM_STATES.SAVING);
  const [deleteErrors, setDeleteErrors] = useState(null);

  const showAllValidationErrors = !_.isEmpty(deleteErrors);
  const areBeaconsActive = showAllValidationErrors && !isSaving;

  const UnmappedFormServerErrors = withControlErrorHandling(UnmappedFormErrors);

  const onClickCreateNewAd = () => {
    send({
      type: WIZARD_EVENTS.SAVE_AND_GOTO,
      flowFactors: { isAdStepActive: true },
      stepId: LINKEDIN_WIZARD_STEP_PATHS.LINKEDIN_AD,
      wizardData: {
        editingAdId: null,
      },
    });
  };

  const onDeleteAd = (adId) => {
    const adToDelete = mapLinkedInAdToBulkModel(
      { Id: campaignId },
      { Id: adGroupId },
      ads[adId],
      true
    );

    createOrUpdateAd(adToDelete).then(() => {
      const adsWithoutDeletedAd = _.omit(ads, adId);

      if (_.isEmpty(adsWithoutDeletedAd)) {
        send({
          type: WIZARD_EVENTS.SAVE_AND_GOTO,
          flowFactors: { isMultiAdModel: false },
          stepId: LINKEDIN_WIZARD_STEP_PATHS.LINKEDIN_AD,
          wizardData: {
            ads: adsWithoutDeletedAd,
            editingAdId: null,
          },
        });
      } else {
        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
          ads: adsWithoutDeletedAd,
        });

        onDataChange({
          data: {
            ...data,
            ads: adsWithoutDeletedAd,
          },
        });
      }
    }).catch((error) => {
      const { errors } = error || {};
      if (!_.isEmpty(errors)) {
        const serverErrors
          = mapBulkUpsertErrors(errors, bulkUpsertEntityErrorMap, _.identity, null, i18n);

        setDeleteErrors([serverErrors]);
      }

      setDeleteErrors([genericApiError]);
    });
  };

  const onEditAd = (adId) => {
    send({
      type: WIZARD_EVENTS.SAVE_AND_GOTO,
      flowFactors: { isAdStepActive: true },
      stepId: LINKEDIN_WIZARD_STEP_PATHS.LINKEDIN_AD,
      wizardData: {
        editingAdId: adId,
      },
    });
  };

  return (
    <div className={classNames('linkedin-ad-manager-step', classes.container)}>
      <div>
        <StepHeader
          title={_TL_('Ads')}
          description={_TL_('Create more LinkedIn ads for this campaign.')}
        />
      </div>
      <div>
        <ActuationGroup actuationProps={{ isActive: areBeaconsActive }}>
          <AttentionBeaconGroup>
            <FormErrorGroup formErrorProps={{ errors: deleteErrors, showAllValidationErrors }}>
              <UnmappedFormServerErrors className="unmapped-server-errors" />
              <MultiAdsManager
                ads={ads || {}}
                onCreateAd={onClickCreateNewAd}
                onDeleteAd={onDeleteAd}
                onEditAd={onEditAd}
                AdPreview={AdPreview}
                extraData={connectedCompanyPage}
              />
            </FormErrorGroup>
          </AttentionBeaconGroup>
        </ActuationGroup>
      </div>

    </div>
  );
};

LinkedInAdsManagerStep.propTypes = {
  data: PropTypes.shape({
    ads: PropTypes.shape({}).isRequired,
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
};
